import { DownloadOutlined, EyeInvisibleOutlined, ExportOutlined } from "@ant-design/icons";
import { Button, Spin, Typography } from "antd";
import mergeClassNames from "common/mergeClassNames";
import { getFileCategory, getFileTypeIcon, getOfficeViewerUrl } from "./utils";

const { Text } = Typography;

/**
 * Renders the body of a single attachment consistently across every viewing
 * mode (overlay / modal / drawer / floating). The chrome around it (toolbar,
 * carousel, navigation) lives in AttachmentViewer; this component only knows
 * how to display one file given a pre-signed URL and the current view state.
 */
const AttachmentContent = ({
    file,
    url,
    loading,
    masked = false,
    tone = "dark",
    zoom = 1,
    rotation = 0,
    position = { x: 0, y: 0 },
    isDragging = false,
    onMouseDown,
    onDownload,
    onOpenNewTab,
}) => {
    const isDark = tone === "dark";
    const primaryText = isDark ? "text-white" : "text-gray-800";
    const secondaryText = isDark ? "text-gray-300" : "text-gray-500";
    const category = getFileCategory(file);

    if (masked) {
        return (
            <div className="flex h-full w-full items-center justify-center p-6 text-center">
                <div>
                    <EyeInvisibleOutlined className={mergeClassNames("mb-4 text-5xl", secondaryText)} />
                    <Text className={mergeClassNames("block text-lg", primaryText)}>This document is masked PII</Text>
                    <Text className={mergeClassNames("block text-sm", secondaryText)}>Preview is disabled because you are not authorised to view it.</Text>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Spin size="large" />
            </div>
        );
    }

    if (!url) {
        return (
            <div className="flex h-full w-full items-center justify-center p-6 text-center">
                <Text className={mergeClassNames("text-base", secondaryText)}>Unable to load this attachment.</Text>
            </div>
        );
    }

    if (category === "image") {
        return (
            <div
                className="flex h-full w-full items-center justify-center overflow-hidden"
                onMouseDown={onMouseDown}
                style={{ cursor: isDragging ? "grabbing" : zoom > 1 ? "grab" : "default" }}
            >
                <img
                    src={url}
                    alt={file?.name || "Attachment"}
                    className="max-h-full max-w-full select-none object-contain"
                    style={{
                        transform: `scale(${zoom}) rotate(${rotation}deg) translate(${position.x}px, ${position.y}px)`,
                        transition: isDragging ? "none" : "transform 0.2s ease",
                    }}
                    draggable={false}
                />
            </div>
        );
    }

    if (category === "video") {
        return (
            <div className="flex h-full w-full items-center justify-center overflow-hidden">
                <video src={url} controls className="max-h-full max-w-full rounded-lg bg-black" controlsList="nodownload">
                    Your browser does not support inline video playback.
                </video>
            </div>
        );
    }

    if (category === "pdf") {
        return (
            <iframe
                src={`${url}#toolbar=1&navpanes=0&view=FitH`}
                title={file?.name || "PDF preview"}
                className="h-full w-full rounded-lg bg-white"
                style={{ border: "none" }}
            />
        );
    }

    if (category === "office") {
        return (
            <iframe
                src={getOfficeViewerUrl(url)}
                title={file?.name || "Document preview"}
                className="h-full w-full rounded-lg bg-white"
                style={{ border: "none" }}
            />
        );
    }

    if (category === "text") {
        return (
            <iframe
                src={url}
                title={file?.name || "Text preview"}
                className="h-full w-full rounded-lg bg-white"
                style={{ border: "none" }}
            />
        );
    }

    // Unknown / unsupported types: offer download + open in new tab.
    return (
        <div className="flex h-full w-full items-center justify-center p-6 text-center">
            <div>
                <div className="mb-4 text-6xl">{getFileTypeIcon(file?.type || "")}</div>
                <Text className={mergeClassNames("mb-4 block text-lg", primaryText)}>Inline preview is not available for this file type.</Text>
                <div className="mt-4 flex justify-center gap-2">
                    <Button type="primary" icon={<DownloadOutlined />} onClick={onDownload}>
                        Download
                    </Button>
                    <Button icon={<ExportOutlined />} onClick={onOpenNewTab}>
                        Open in new tab
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AttachmentContent;
